import React from 'react';
import { Bell, Check, X, AlertCircle, Gift, Megaphone, FileText, HelpCircle, PackageCheck } from 'lucide-react';

const NotificationDropdown = ({ isOpen, onClose, notifications = [], onNotificationClick }) => {
  // 읽지 않은 알림 개수
  const unreadCount = notifications.length; // 모두 읽지 않은 알림

  // 게시물 타입별 아이콘
  const getIcon = (postType) => {
    switch (postType) {
      case 'events':
        return <Gift size={20} color="#059669" />;
      case 'update_and_notice':
        return <PackageCheck size={20} color="#7c3aed" />;
      case 'FAQ':
        return <HelpCircle size={20} color="#2563eb" />;
      default:
        return <Bell size={20} color="#6b7280" />;
    }
  };

  // 시간 포맷 함수
  const formatTime = (createdAt) => {
    const now = new Date();
    const time = new Date(createdAt);
    const diff = Math.floor((now - time) / 1000); // 초 단위

    if (diff < 60) return '방금 전';
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;
    
    return time.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* 오버레이 */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 998,
        }}
        onClick={onClose}
      />
      
      {/* 드롭다운 */}
      <div
        style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: '380px',
          maxWidth: '90vw',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
          zIndex: 999,
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #f3f4f6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h3
              style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '700',
                color: '#1a1a1a',
              }}
            >
              알림
            </h3>
            {unreadCount > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '20px',
                  height: '20px',
                  padding: '0 6px',
                  borderRadius: '10px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '700',
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              border: 'none',
              background: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {/* 알림 리스트 */}
        <div
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {notifications.length === 0 ? (
            <div
              style={{
                padding: '60px 20px',
                textAlign: 'center',
                color: '#9ca3af',
              }}
            >
              <Bell size={48} color="#d1d5db" style={{ marginBottom: '12px' }} />
              <p style={{ margin: 0, fontSize: '14px' }}>새로운 알림이 없습니다</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => onNotificationClick && onNotificationClick(notification)}
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  backgroundColor: '#f0fdf4',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dcfce7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0fdf4';
                }}
              >
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  {/* 아이콘 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#ffffff',
                      flexShrink: 0,
                    }}
                  >
                    {getIcon(notification.postType)}
                  </div>

                  {/* 내용 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {notification.category && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: '#16bf9f',
                          fontWeight: '600',
                          marginBottom: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {notification.category}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: '#1a1a1a',
                        marginBottom: '4px',
                        lineHeight: '1.5',
                      }}
                    >
                      {notification.title}
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        marginBottom: '6px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {notification.message}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#9ca3af',
                      }}
                    >
                      {formatTime(notification.createdAt)}
                    </div>
                  </div>

                  {/* 읽지 않음 표시 (항상 표시) */}
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#16bf9f',
                      flexShrink: 0,
                      marginTop: '6px',
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {/* 푸터 (모두 읽음 처리 버튼) */}
        {notifications.length > 0 && unreadCount > 0 && (
          <div
            style={{
              padding: '12px 20px',
              borderTop: '1px solid #f3f4f6',
              backgroundColor: '#f9fafb',
            }}
          >
            <button
              style={{
                width: '100%',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#16bf9f',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e6f7f4';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <Check size={16} />
              모두 읽음 처리
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationDropdown;

